import { useContext } from 'react';
import styled from 'styled-components';
import i18n from 'src/utils/translate';
import { useDispatch, useSelector } from 'src/store/store';
import { titleMedium, textLight, text } from 'styles-js/mixins/typography';
import { Select } from 'styles-js/form';
import { emailTypes, digestFrequencies, digestIds } from 'src/modules/unsubscribe/unsubscribeUtils';
import { showModal } from 'src/modules/shared/modal/actions';
import { updateEmailPreferences } from 'src/api/emailNotifications';
import ErrorBanner from 'src/modules/unsubscribe/errorBanner';
import { setAlert } from 'src/components/alert/actions';
import { NotificationsContext } from 'pages/emailNotifications';
import { ModalType } from 'src/constants/modalTypes';
import { Notification } from 'src/constants/notifications';

export default function EmailTypeSection({ title, emailType }: { title: string, emailType: string }) {
  const dispatch = useDispatch();
  const { notifications, token, refreshData } = useContext(NotificationsContext);
  const { currentUser } = useSelector((state) => state.context);

  const emails = emailTypes.filter(type => type.emailType === emailType);
  const text = emailType === 'HealthUnlocked'
    ? i18n.t('Email me about:')
    : i18n.t('Email me when:');

  const onCheckboxChange = (notificationId: number, enabled: boolean) => {
    if (notificationId === Notification.NewPost && enabled) {
      dispatch(showModal({
        modalType: ModalType.EmailSubscribeAllPosts,
        modalProps: {
          onAccept: () => updateEmailNotification(notificationId, enabled),
        },
      }));
    } else {
      updateEmailNotification(notificationId, enabled);
    }
  };

  const updateEmailNotification = async (notificationId: number, enabled: boolean) => {
    const email = [{ notificationId, enabled }];

    const updatedPreferences = await updateEmailPreferences(email, token, currentUser?.userId);

    if (updatedPreferences) {
      refreshData(updatedPreferences);
      dispatch(setAlert(i18n.t('Your preference has been updated!'), 'success', 6000));
    } else {
      dispatch(setAlert(<ErrorBanner />, 'danger', 6000));
    }
  };

  const updateDigestOptions = async (id: number) => {
    const digestOptions = digestFrequencies.map((option) => {
      const isEnabled = id === option.notificationId;
      return {
        notificationId: option.notificationId,
        enabled: isEnabled,
      };
    });

    const updatedPreferences = await updateEmailPreferences(digestOptions, token, currentUser?.userId);

    if (updatedPreferences) {
      refreshData(updatedPreferences);
      dispatch(setAlert(i18n.t('Your preference has been updated!'), 'success', 6000));
    } else {
      dispatch(setAlert(<ErrorBanner />, 'danger', 6000));
    }
  };

  let digestOptionSelected;

  if (emailType === 'Community') {
    const enabledOption = notifications?.find((option) => digestIds.includes(option.notificationId) && !!option.enabled);
    digestOptionSelected = enabledOption?.notificationId || 0;
  }

  return (
    <Section key={emailType}>
      <Title>{title}</Title>
      {emailType === 'Community' && (
        <Dropdown>
          <span>{i18n.t('Digest frequency:')}</span>
          <StyledSelect
            data-testid="digest-frequency"
            value={digestOptionSelected}
            onChange={(e) => updateDigestOptions(parseInt(e.target.value))}
          >
            {digestFrequencies.map(option => <option key={option.notificationId} value={option.notificationId}>{option.text}</option>)}
            <option key={0} value={0}>{i18n.t('Never')}</option>
          </StyledSelect>
        </Dropdown>
      )}
      <GreyText>{text}</GreyText>
      {emails.map((email) => {
        const isEnabled = notifications?.find(({ notificationId }) => notificationId === email.notificationId)?.enabled;
        return (
          <Label key={email.notificationId}>
            <Checkbox
              checked={isEnabled}
              data-testid={email.notificationType}
              type="checkbox"
              onChange={() => {
                onCheckboxChange(email.notificationId, !isEnabled);
              }}
            />
            <span>{email.text}</span>
          </Label>
        );
      },
      )}
    </Section>
  );
}

const Section = styled.div`
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colorGreyLight};
`;

const Title = styled.div`
  ${titleMedium};
  padding: 15px 0;
`;

const GreyText = styled.div`
  ${textLight};
  padding-bottom: 5px;
`;

const Label = styled.label`
  ${text};
  display: flex;
  margin: 8px 0 0 0;
  cursor: pointer;
`;

const Checkbox = styled.input`
  height: 17px;
  min-width: 17px;
  margin-top: 2px;
  margin-right: 8px;
  cursor: pointer;
`;

const Dropdown = styled.div`
  margin-bottom: 15px;
`;

const StyledSelect = styled(Select)`
  margin-left: 10px;
`;
